import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { observer } from 'mobx-react';
import axios from 'axios';
import TutorialContext from '../Tutorial/TutorialContext';
import WelcomeModal from '../TrainingPlan/WelcomeModal';
import TrainingPlanPanel from '../TrainingPlan/TrainingPlanPanel';
import { computePlanState, isInteractiveAssessment } from '../TrainingPlan/planState';
import { calculateUserAchievements } from '../../utils/achievementCalculation';
import AchievementNotification from '../../components/AchievementNotification/AchievementNotification';
import ProgressChart from '../../components/Dashboard/ProgressChart';
import GoalProgress from '../../components/Dashboard/GoalProgress';
import DashboardNudges from '../../components/Dashboard/DashboardNudges';
import RecentMeetingsWidget from '../../components/Dashboard/RecentMeetingsWidget';
import LeaderboardWidget from '../../components/Dashboard/LeaderboardWidget';
import NewAssessmentDetails from '../../components/PerformanceReviews/NewAssessmentDetails';
import { showsQuestionBreakdown, canSaveQuestionToLibrary } from '../../components/PerformanceReviews/questionBreakdown';
import { filterBySessionType, STREAK_MESSAGES, NO_STREAK_MESSAGES, hasRole, MANAGER_ROLES, EXECUTIVE_ROLES } from '../../pages/dashboardHelpers';
import GoalSettings from '../../components/Dashboard/GoalSettings';
import StatsOverview from './StatsOverview';
import TrainingCard from './TrainingCard';
import ResponseTrainingCard from './ResponseTrainingCard';
import DocumentUploadModal from '../../components/RecordPresentation/DocumentUploadModal';
import ProspectPrepLoadingModal from '../../components/RecordPresentation/ProspectPrepLoadingModal';
import {
  DashboardWrapper, WelcomeSection, WelcomeTitle, WelcomeSubtitle,
  StreakBadge, SectionTitle, TrainingGrid,
  FeedbackPanel, FeedbackHeader, FeedbackTitle, CloseButton, EmptyState,
  AchievementsSection, AchievementsGrid, AchievementCard,
  AchievementIcon, AchievementName, AchievementDesc, ShowMoreButton,
} from './DashboardPage.styles';

const DashboardPageV2 = observer(({ usersStore, companyStore, assessmentsStore, meetingStore, myMeetingsStore, leaderboardStore, history }) => {
  const [expandedFeedbackSection, setExpandedFeedbackSection] = useState(null);
  const [availableAchievementsOpen, setAvailableAchievementsOpen] = useState(true);
  const [newAchievement, setNewAchievement] = useState(null);
  const [showAchievementNotification, setShowAchievementNotification] = useState(false);
  const [goalSettingsOpen, setGoalSettingsOpen] = useState(false);
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [prospectPrepLoading, setProspectPrepLoading] = useState(false);
  const [prospectPrepError, setProspectPrepError] = useState('');
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const welcomeLatchRef = useRef(false);
  const planCompletedStampRef = useRef(false);
  const tutorialCtx = useContext(TutorialContext);

  const startObjectionHandling = async () => {
    setQaLoading(true);
    try {
      await meetingStore.initQASession(companyStore.company.id, 5);
      history.push('/record-presentation/objection-handling');
    } catch (err) {
      console.error('Failed to start Q&A session:', err);
      setProspectPrepError(err.message || 'Failed to start the session. Please try again.');
      setQaLoading(false);
    }
  };

  const startProspectPrep = async () => {
    setProspectPrepLoading(true);
    setProspectPrepError('');
    try {
      await meetingStore.initProspectPrepSession(companyStore.company.id, 5);
      history.push('/record-presentation/messaging-defense');
    } catch (err) {
      console.error('Failed to start Messaging Defense session:', err);
      setProspectPrepError(err.message || 'Failed to start Messaging Defense session. Please try again.');
      setProspectPrepLoading(false);
    }
  };

  const handleDocumentQA = async (file) => {
    const companyId = companyStore.company.id;
    const filename = `${Date.now()}-${file.name}`;
    const contentType = file.type || 'application/octet-stream';
    const headers = { 'Content-Type': contentType };

    const uploadResult = await axios.post(
      `/companies/${companyId}/uploads/doc-qa%2F${encodeURIComponent(filename)}`,
      {},
      { headers }
    );
    const uploadUrl = uploadResult.data.uploadUrl;
    const documentKey = uploadResult.data.key;

    await axios.put(uploadUrl, file, { headers, baseURL: '' });
    await meetingStore.initDocumentQASession(companyId, 5, documentKey);
    history.push('/record-presentation/document-defense');
  };

  const startRecording = async (sessionType, isInteractive = false) => {
    const recordingKeyMap = {
      'Elevator Pitch': isInteractive ? 'elevator_pitch_2' : 'elevator_pitch',
      'Casual Encounter': isInteractive ? 'casual_encounter_2' : 'casual_encounter',
      'Sales Presentation': isInteractive ? 'sales_presentation_2' : 'sales_presentation',
    };
    const recordingKey = recordingKeyMap[sessionType];
    if (recordingKey) {
      try {
        await meetingStore.setMeetingType(recordingKey, companyStore.company.id);
        history.push(`/record-presentation/${recordingKey}`);
      } catch (err) {
        // setMeetingType now throws on construction failure (it used to
        // swallow, which navigated to a broken/stale meeting). Reuse the
        // bottom toast so the user sees why nothing happened.
        console.error('Failed to start recording session:', err);
        setProspectPrepError(err.message || 'Failed to start the session. Please try again.');
      }
    }
  };

  const currentUser = usersStore?.currentUser;
  const isStoreLoading = usersStore?.isLoading || assessmentsStore?.isLoading;
  const isDataReady = usersStore && currentUser && currentUser.userId;

  let currentUserStreakData = null;
  let isOnStreak = false;
  let userAssessments = [];

  try {
    if (usersStore?.getCurrentUserStreakStatus && assessmentsStore) {
      currentUserStreakData = usersStore.getCurrentUserStreakStatus(assessmentsStore, companyStore?.company?.id);
    }
    if (usersStore?.isUserOnStreak && assessmentsStore && currentUser?.userId) {
      isOnStreak = usersStore.isUserOnStreak(currentUser.userId, assessmentsStore, companyStore?.company?.id);
    }
    if (assessmentsStore?.closedAssessmentsForUser && currentUser?.userId) {
      userAssessments = assessmentsStore.closedAssessmentsForUser(currentUser.userId, companyStore?.company?.id);
    }
  } catch (error) {
    console.error('Error loading user data:', error);
  }

  // Training Plan (guided onboarding) — behavior-derived, so a failure here
  // must never take down the dashboard.
  let planState = null;
  try {
    planState = computePlanState({ currentUser, company: companyStore?.company, assessmentsStore });
  } catch (error) {
    console.error('Error computing training plan state:', error);
  }

  const achievements = useMemo(() => {
    // Same rule as streaks/plan state above: a bad assessment record (e.g. a
    // malformed timestamp) must never white-screen the dashboard.
    try {
      return calculateUserAchievements(userAssessments || [], currentUserStreakData);
    } catch (error) {
      console.error('Error calculating achievements:', error);
      return { earned: [], available: [] };
    }
  }, [userAssessments, currentUserStreakData]);

  useEffect(() => {
    if (!achievements?.earned?.length || !currentUser?.userId) return;
    try {
      const storageKey = `earnedAchievements_${currentUser.userId}`;
      const storedRaw = localStorage.getItem(storageKey);
      const currentEarnedIds = achievements.earned.map(a => a.id);
      if (storedRaw === null) {
        // First load on this browser: seed everything already earned as seen
        // WITHOUT toasting — popping one historic achievement (and silently
        // marking the rest seen) on every new device was pure noise.
        localStorage.setItem(storageKey, JSON.stringify(currentEarnedIds));
        return;
      }
      const previousEarnedIds = JSON.parse(storedRaw) || [];
      const newlyEarned = achievements.earned.filter(a => !previousEarnedIds.includes(a.id));
      if (newlyEarned.length > 0) {
        setNewAchievement(newlyEarned[0]);
        setShowAchievementNotification(true);
        localStorage.setItem(storageKey, JSON.stringify(currentEarnedIds));
      }
    } catch (error) {
      // Blocked/broken storage must not crash the dashboard.
      console.error('Achievement notification storage failed:', error);
    }
  }, [achievements, currentUser?.userId]);

  const streakMotivation = useMemo(() => STREAK_MESSAGES[Math.floor(Math.random() * STREAK_MESSAGES.length)], []);
  const noStreakMotivation = useMemo(() => NO_STREAK_MESSAGES[Math.floor(Math.random() * NO_STREAK_MESSAGES.length)], []);

  // Show the Training Plan welcome once, ever. The latch keeps the modal open
  // while the welcome-seen PATCH lands (which flips welcomeEligible false), and
  // the sessionStorage guard stops a same-session re-fire if that PATCH fails.
  useEffect(() => {
    if (!planState?.welcomeEligible) return;
    // Per-user key: a shared browser tab (logout → different login) must not
    // let user A's welcome consume user B's one-time showing. The assignment
    // anchor is in the key too, so a manager-assigned refresher run re-fires
    // even if this session already showed the organic welcome. The latch holds
    // the KEY (not a boolean) so a new anchor can re-fire within this mount.
    const welcomeGuardKey = `multiply_tp_welcome_shown_${currentUser?.userId}_${planState?.assignedAt || 'organic'}`;
    if (welcomeLatchRef.current === welcomeGuardKey) return;
    let alreadyShown = null;
    try { alreadyShown = sessionStorage.getItem(welcomeGuardKey); } catch { /* storage blocked */ }
    if (alreadyShown) return;
    try { sessionStorage.setItem(welcomeGuardKey, '1'); } catch { /* storage blocked */ }
    welcomeLatchRef.current = welcomeGuardKey;
    setWelcomeOpen(true);
    if (currentUser?.setTrainingPlanWelcomeSeen) {
      currentUser.setTrainingPlanWelcomeSeen().catch((err) => console.error('Failed to record Training Plan welcome:', err));
    }
  }, [planState?.welcomeEligible, planState?.assignedAt, currentUser]);

  // Stamp completion once when the derived plan finishes (analytics timestamp;
  // panelVisible gate keeps replay-only "completions" by veterans unstamped).
  useEffect(() => {
    if (planCompletedStampRef.current) return;
    // needsCompletionStamp is anchor-aware: for a manager-assigned re-run, an
    // OLD pre-assignment completion stamp doesn't block stamping the new run.
    if (!planState?.panelVisible || !planState.needsCompletionStamp) return;
    if (!currentUser?.userId || !currentUser.setTrainingPlanCompleted) return;
    planCompletedStampRef.current = true;
    currentUser.setTrainingPlanCompleted().catch((err) => console.error('Failed to record Training Plan completion:', err));
  }, [planState?.needsCompletionStamp, planState?.panelVisible, currentUser]);

  const dismissTrainingPlan = () => {
    if (currentUser?.setTrainingPlanDismissed) {
      currentUser.setTrainingPlanDismissed().catch((err) => console.error('Failed to dismiss Training Plan:', err));
    }
  };

  // Runs one launcher — shared by plan slots and the graduation recommendations.
  // Practice launchers reuse the exact handlers the training cards use, so
  // loading modals and error toasts keep working.
  const runLauncher = (chosen) => {
    if (!chosen) return undefined;
    if (chosen.kind === 'prospectPrep') return startProspectPrep();
    if (chosen.kind === 'qaSession') return startObjectionHandling();
    if (chosen.kind === 'docDefense') return setShowDocUploadModal(true);
    if (chosen.kind === 'recording') return startRecording(chosen.sessionType, !!chosen.interactive);
    return undefined;
  };

  const launchTrainingSlot = async (row, launcher) => {
    const slot = row?.slot;
    if (!slot) return;
    if (slot.kind === 'review') {
      if (currentUser?.markTrainingPlanCheck) {
        currentUser.markTrainingPlanCheck(slot.id).catch((err) => console.error('Failed to record review step:', err));
      }
      // Open the reading in a new tab so the guided plan panel stays put on the
      // dashboard underneath — a new user shouldn't lose their place.
      window.open(slot.link, '_blank', 'noopener,noreferrer');
      return;
    }
    runLauncher(launcher || row.availableLaunchers?.[0]);
  };

  // Compute stats. Total Sessions counts PRACTICE sessions only — live-meeting
  // reports are excluded so this tile agrees with streaks/achievements (both
  // count practice types) instead of silently disagreeing on the same screen.
  const practiceAssessments = userAssessments.filter(a => a.sessionType !== 'Live Meeting');
  const totalSessions = practiceAssessments.length;
  const currentStreak = currentUserStreakData?.currentStreak || 0;
  const weeklyTarget = companyStore?.company?.practiceGoals?.weeklyTarget;
  const now = new Date();
  const weekStart = new Date(now);
  const dayOfWeek = now.getUTCDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  weekStart.setUTCDate(now.getUTCDate() - mondayOffset);
  weekStart.setUTCHours(0, 0, 0, 0);
  const thisWeekCount = practiceAssessments.filter(a => new Date(a.completedDateTime) >= weekStart).length;
  const weeklyGoalPct = weeklyTarget > 0 ? Math.min(100, Math.round((thisWeekCount / weeklyTarget) * 100)) : null;
  const canSetGoals = hasRole(currentUser, MANAGER_ROLES) || hasRole(currentUser, EXECUTIVE_ROLES);

  // Response Training stats. Month boundary is UTC to match the weekly stats
  // above (was local — the one calendar definition on this page that differed).
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const computeResponseStats = (sessionType) => {
    const typeAssessments = filterBySessionType(userAssessments, sessionType);
    const total = typeAssessments.length;
    let statusText = 'Not Started';

    if (total > 0) {
      const sorted = [...typeAssessments].sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
      const lastDate = new Date(sorted[0].completedDateTime);
      const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const lastMidnight = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
      const daysDiff = Math.round((todayMidnight - lastMidnight) / (1000 * 60 * 60 * 24));
      if (daysDiff === 0) statusText = 'Completed Today';
      else if (daysDiff === 1) statusText = 'Completed Yesterday';
      else if (daysDiff <= 30) statusText = `${daysDiff} days ago`;
      else statusText = `${Math.floor(daysDiff / 30)}mo ago`;
    }

    const thisMonth = typeAssessments.filter(a => new Date(a.completedDateTime) >= monthStart).length;

    return { totalSessions: total, statusText, thisMonth };
  };

  const objectionStats = computeResponseStats('Objection Handling');
  const dealCoachingStats = computeResponseStats('Document Defense');
  const prospectPrepStats = computeResponseStats('Messaging Defense');

  if (isStoreLoading || !isDataReady) {
    return (
      <DashboardWrapper>
        <WelcomeSection>
          <WelcomeTitle>{isStoreLoading ? 'Loading...' : 'Loading user data...'}</WelcomeTitle>
        </WelcomeSection>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      {/* The Training Plan is the single call-to-action while it's active —
          nudges resume as the steady-state motivator once it retires. */}
      {!planState?.panelVisible && (
        <DashboardNudges
          assessments={userAssessments}
          streakData={currentUserStreakData || {}}
          goals={companyStore?.company?.practiceGoals}
          onStartRecording={(sessionType) => startRecording(sessionType || 'Elevator Pitch')}
          userId={currentUser?.userId}
        />
      )}

      <WelcomeSection>
        <WelcomeTitle>
          Welcome back, {currentUser.firstName}
          {currentStreak > 0 && (
            <StreakBadge $isOnStreak={isOnStreak}>
              {'\u{1F525}'} {currentStreak} week streak
            </StreakBadge>
          )}
        </WelcomeTitle>
        <WelcomeSubtitle>
          {planState?.panelVisible && !planState.planComplete
            ? (planState.todayComplete
              ? `Day ${planState.currentDayNumber} of your Training Plan is done. See you tomorrow!`
              : `You're on Day ${planState.currentDayNumber} of your Training Plan: ${planState.todayDoneCount} of ${planState.todayRequiredCount} sessions done today`)
            : (isOnStreak ? streakMotivation : noStreakMotivation)}
        </WelcomeSubtitle>
      </WelcomeSection>

      {planState?.panelVisible && (
        <TrainingPlanPanel
          planState={planState}
          currentUser={currentUser}
          company={companyStore?.company}
          onLaunch={launchTrainingSlot}
          onRecommendation={runLauncher}
          onDismiss={dismissTrainingPlan}
        />
      )}

      <StatsOverview
        totalSessions={totalSessions}
        currentStreak={currentStreak}
        weeklyGoalPct={weeklyGoalPct}
        canSetGoals={canSetGoals}
        onSetGoals={canSetGoals ? () => setGoalSettingsOpen(true) : undefined}
      />

      {myMeetingsStore && companyStore.company?.liveMeetingsEnabled && (
        <RecentMeetingsWidget
          companyStore={companyStore}
          myMeetingsStore={myMeetingsStore}
        />
      )}

      {leaderboardStore && (companyStore.company?.leaderboardEnabled || currentUser?.isCompanyAdmin || currentUser?.isAdmin) && (
        <LeaderboardWidget
          companyStore={companyStore}
          usersStore={usersStore}
          leaderboardStore={leaderboardStore}
        />
      )}

      <SectionTitle>Pitch Training</SectionTitle>
      <TrainingGrid data-tour="training-grid">
        {['Elevator Pitch', 'Casual Encounter', 'Sales Presentation'].map((sessionType, idx) => {
          const typeAssessments = filterBySessionType(userAssessments, sessionType);
          const lastCompleted = typeAssessments.length > 0
            ? [...typeAssessments].sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime))[0]
            : null;
          const lastCompletedDate = lastCompleted ? new Date(lastCompleted.completedDateTime) : null;

          let standardCount = 0;
          let interactiveCount = 0;
          typeAssessments.forEach(a => {
            // Canonical L1-vs-L2 heuristic lives in planState.js so this count
            // and the Training Plan can never disagree about what "interactive"
            // means. (Earlier this filter read a.recordingKey, which doesn't
            // exist on the assessment model, so Interactive was always 0.)
            if (isInteractiveAssessment(a)) {
              interactiveCount++;
            } else {
              standardCount++;
            }
          });

          let statusText = 'Not Started';
          if (lastCompleted && lastCompletedDate) {
            const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const lastMid = new Date(lastCompletedDate.getFullYear(), lastCompletedDate.getMonth(), lastCompletedDate.getDate());
            const daysDiff = Math.round((todayMid - lastMid) / (1000 * 60 * 60 * 24));
            if (daysDiff === 0) statusText = 'Completed Today';
            else if (daysDiff === 1) statusText = 'Completed Yesterday';
            else if (daysDiff <= 30) statusText = `${daysDiff} days ago`;
            else statusText = `${Math.floor(daysDiff / 30)}mo ago`;
          }

          const exampleVideoKey = { 'Elevator Pitch': 'elevatorPitch', 'Casual Encounter': 'casualEncounter' }[sessionType];
          const exampleVideo = exampleVideoKey ? companyStore?.company?.trainingPlanVideos?.[exampleVideoKey] : undefined;
          const card = (
            <TrainingCard
              key={sessionType}
              sessionType={sessionType}
              totalPractices={typeAssessments.length}
              standardCount={standardCount}
              interactiveCount={interactiveCount}
              statusText={statusText}
              userAssessments={userAssessments}
              exampleVideo={exampleVideo}
              onRecordStandard={() => startRecording(sessionType, false)}
              onRecordInteractive={() => startRecording(sessionType, true)}
              onViewFeedback={() => setExpandedFeedbackSection(
                expandedFeedbackSection === sessionType ? null : sessionType
              )}
              isExpanded={expandedFeedbackSection === sessionType}
            />
          );
          if (idx === 0) return <div key={sessionType} data-tour="training-card-first" style={{display: 'flex'}}>{card}</div>;
          return card;
        })}
      </TrainingGrid>

      {/* Pitch Feedback Section */}
      {expandedFeedbackSection && !showsQuestionBreakdown(expandedFeedbackSection) && (() => {
        const typeAssessments = filterBySessionType(userAssessments, expandedFeedbackSection)
          .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
        return (
          <FeedbackPanel>
            <FeedbackHeader>
              <FeedbackTitle>{expandedFeedbackSection} Feedback</FeedbackTitle>
              <CloseButton onClick={() => setExpandedFeedbackSection(null)}>
                {'\u2715'}
              </CloseButton>
            </FeedbackHeader>
            {typeAssessments.length === 0 ? (
              <EmptyState>
                No {expandedFeedbackSection.toLowerCase()} assessments completed yet.
                Complete your first assessment to see feedback here.
              </EmptyState>
            ) : (
              <NewAssessmentDetails assessments={typeAssessments} />
            )}
          </FeedbackPanel>
        );
      })()}

      <SectionTitle>Response Training</SectionTitle>
      <TrainingGrid data-tour="response-training">
        {/* Messaging Defense */}
        <ResponseTrainingCard
          sessionType="Messaging Defense"
          totalSessions={prospectPrepStats.totalSessions}
          thisMonth={prospectPrepStats.thisMonth}
          statusText={prospectPrepStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Start Practice"
          onAction={startProspectPrep}
          isLoading={prospectPrepLoading}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Messaging Defense' ? null : 'Messaging Defense'
          )}
          isExpanded={expandedFeedbackSection === 'Messaging Defense'}
        />
        <ResponseTrainingCard
          sessionType="Objection Handling"
          totalSessions={objectionStats.totalSessions}
          thisMonth={objectionStats.thisMonth}
          statusText={objectionStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Start Practice"
          onAction={startObjectionHandling}
          isLoading={qaLoading}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Objection Handling' ? null : 'Objection Handling'
          )}
          isExpanded={expandedFeedbackSection === 'Objection Handling'}
        />
        <ResponseTrainingCard
          sessionType="Document Defense"
          totalSessions={dealCoachingStats.totalSessions}
          thisMonth={dealCoachingStats.thisMonth}
          statusText={dealCoachingStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Upload Document"
          onAction={() => setShowDocUploadModal(true)}
          isLoading={false}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Document Defense' ? null : 'Document Defense'
          )}
          isExpanded={expandedFeedbackSection === 'Document Defense'}
        />
      </TrainingGrid>

      {/* Response Training Feedback */}
      {expandedFeedbackSection && showsQuestionBreakdown(expandedFeedbackSection) && (() => {
        const typeAssessments = filterBySessionType(userAssessments, expandedFeedbackSection)
          .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
        return (
          <FeedbackPanel>
            <FeedbackHeader>
              <FeedbackTitle>{expandedFeedbackSection} Feedback</FeedbackTitle>
              <CloseButton onClick={() => setExpandedFeedbackSection(null)}>
                {'\u2715'}
              </CloseButton>
            </FeedbackHeader>
            {typeAssessments.length === 0 ? (
              <EmptyState>
                No {expandedFeedbackSection.toLowerCase()} assessments completed yet.
                Complete your first assessment to see feedback here.
              </EmptyState>
            ) : (
              <NewAssessmentDetails
                assessments={typeAssessments}
                canEditLibrary={!usersStore.currentUser.isSalesMember && !usersStore.currentUser.isReadOnlyExecutive && !usersStore.currentUser.isReadOnlySalesManager && !usersStore.currentUser.isReadOnlySeniorManager}
                existingQuestions={companyStore.company.questionsAndAnswers || []}
                onSaveToLibrary={canSaveQuestionToLibrary(expandedFeedbackSection) ? async (questionsToSave) => {
                  const existing = companyStore.company.questionsAndAnswers.map(qa => ({
                    question: qa.question,
                    answer: qa.answer,
                    related_to: qa.related_to,
                    ...(qa.uploadKey ? { uploadKey: qa.uploadKey } : {})
                  }));
                  const newQuestions = questionsToSave.filter(q =>
                    !existing.some(eq => eq.question?.trim() === q.question?.trim())
                  );
                  if (newQuestions.length === 0) return;
                  await companyStore.company.updateQuestionsAndAnswers([...existing, ...newQuestions]);
                } : undefined}
              />
            )}
          </FeedbackPanel>
        );
      })()}

      <GoalProgress assessments={userAssessments} companyStore={companyStore} />
      <ProgressChart assessments={userAssessments} investorPitchEnabled={companyStore?.company?.investorPitchEnabled} />

      {/* Achievements */}
      <AchievementsSection>
        <SectionTitle>Achievements ({achievements.totalEarned})</SectionTitle>
        <AchievementsGrid>
          {achievements.earned.map((achievement) => (
            <AchievementCard key={achievement.id} $earned $color={achievement.color}>
              <AchievementIcon $color={achievement.color} $earned>
                {achievement.icon}
              </AchievementIcon>
              <AchievementName $color={achievement.color} $earned>
                {achievement.name}
              </AchievementName>
              <AchievementDesc>{achievement.description}</AchievementDesc>
            </AchievementCard>
          ))}
        </AchievementsGrid>

        {achievements.available.length > 0 && (
          <>
            <ShowMoreButton onClick={() => setAvailableAchievementsOpen(!availableAchievementsOpen)}>
              {availableAchievementsOpen ? 'Hide' : 'Show'} Available ({achievements.totalAvailable})
            </ShowMoreButton>
            {availableAchievementsOpen && (
              <AchievementsGrid>
                {achievements.available.map((achievement) => (
                  <AchievementCard key={achievement.id} $color={achievement.color}>
                    <AchievementIcon $color={achievement.color}>
                      {achievement.icon}
                    </AchievementIcon>
                    <AchievementName $color={achievement.color}>
                      {achievement.name}
                    </AchievementName>
                    <AchievementDesc>{achievement.description}</AchievementDesc>
                  </AchievementCard>
                ))}
              </AchievementsGrid>
            )}
          </>
        )}
      </AchievementsSection>

      <AchievementNotification
        achievement={newAchievement}
        isVisible={showAchievementNotification}
        onClose={() => {
          setShowAchievementNotification(false);
          setNewAchievement(null);
        }}
      />

      {canSetGoals && (
        <GoalSettings
          companyStore={companyStore}
          open={goalSettingsOpen}
          onClose={() => setGoalSettingsOpen(false)}
        />
      )}

      <DocumentUploadModal
        open={showDocUploadModal}
        onClose={() => setShowDocUploadModal(false)}
        onDocumentQA={handleDocumentQA}
      />

      <WelcomeModal
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        currentUser={currentUser}
        company={companyStore?.company}
        planState={planState}
        onStartNext={(row) => {
          setWelcomeOpen(false);
          launchTrainingSlot(row, null);
        }}
        onShowMeAround={() => {
          setWelcomeOpen(false);
          if (tutorialCtx?.restartTutorial) tutorialCtx.restartTutorial();
        }}
      />

      <ProspectPrepLoadingModal open={prospectPrepLoading} />
      {prospectPrepError && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#dc3545', color: 'white', padding: '12px 24px', borderRadius: 8, zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
          {prospectPrepError}
          <button onClick={() => setProspectPrepError('')} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 'bold' }}>&times;</button>
        </div>
      )}
    </DashboardWrapper>
  );
});

export default DashboardPageV2;
